import { useCallback, useState } from 'react';
import { getBridgeDeletionInfo } from '../../shared/bridgeDeleteConditions';
import { findTaskElement } from '../cardBitmaps/findTaskElement';

/**
 * Asks whether to bridge connections when deleting a node that has both
 * upstream and downstream links. Deletes immediately when bridging is impossible.
 */
export function useBridgeDeletePrompt(tasks, onDeleteTask, onDeleteTasks) {
  const [bridgePrompt, setBridgePrompt] = useState(null);

  const openBridgePromptOrDelete = useCallback((taskIds, execute) => {
    const info = getBridgeDeletionInfo(tasks, taskIds);
    if (!info.canBridge) {
      execute(false);
      return;
    }

    let position = null;
    for (const id of taskIds) {
      const el = findTaskElement(id);
      if (el) {
        const rect = el.getBoundingClientRect();
        position = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
        break;
      }
    }
    if (!position) {
      position = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    }

    setBridgePrompt({ taskIds, position, info, execute });
  }, [tasks]);

  const handleDeleteTaskWithBridgeCheck = useCallback((taskId, options) => {
    if (!onDeleteTask) return;
    if (options?.skipBridgePrompt) {
      onDeleteTask(taskId, options);
      return;
    }
    openBridgePromptOrDelete([taskId], (bridge) => onDeleteTask(taskId, { ...options, bridge }));
  }, [onDeleteTask, openBridgePromptOrDelete]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteTasksWithBridgeCheck = useCallback((taskIds, options) => {
    if (!onDeleteTasks) return;
    if (options?.skipBridgePrompt) {
      onDeleteTasks(taskIds, options);
      return;
    }
    openBridgePromptOrDelete(taskIds, (bridge) => onDeleteTasks(taskIds, { ...options, bridge }));
  }, [onDeleteTasks, openBridgePromptOrDelete]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolveBridgePrompt = useCallback((bridge) => {
    const prompt = bridgePrompt;
    setBridgePrompt(null);
    if (prompt) prompt.execute(bridge);
  }, [bridgePrompt]);

  return {
    bridgePrompt,
    setBridgePrompt,
    handleDeleteTaskWithBridgeCheck,
    handleDeleteTasksWithBridgeCheck,
    resolveBridgePrompt,
  };
}
